import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Wraps an async action with a re-entry guard. `run()` ignores calls while a
 * previous one is still pending; the guard is a ref (not state) so a double
 * click landing inside one frame - before React re-renders - cannot fire twice.
 * `running` is the state mirror, for disabling / spinner UI.
 */
export interface AsyncActionOptions {
  /**
   * Let the browser paint the busy state before `fn` starts. Use when `fn`'s synchronous
   * prologue is heavy (reading a multi-MB textarea, building a big FormData...).
   */
  paintFirst?: boolean;
}

/** Resolves after the next paint (rAF -> macrotask), with a timer fallback for hidden tabs. */
export function afterPaint(): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        resolve();
      }
    };
    window.requestAnimationFrame(() => setTimeout(finish, 0));
    setTimeout(finish, 100);
  });
}

export function useAsyncAction<A extends unknown[], R>(
  fn: (...args: A) => Promise<R> | R,
  options?: AsyncActionOptions,
) {
  const paintFirst = options?.paintFirst === true;
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(async (...args: A): Promise<R | undefined> => {
    if (busyRef.current) return undefined;
    busyRef.current = true;
    setRunning(true);
    try {
      if (paintFirst) await afterPaint();
      return await fnRef.current(...args);
    } finally {
      busyRef.current = false;
      if (mountedRef.current) setRunning(false);
    }
  }, [paintFirst]);

  return { run, running };
}
